import {
  bresenhamLine,
  dilate,
  ellipseFill,
  ellipseOutline,
  floodFill,
  rectFill,
  rectOutline,
} from "#canvas/algorithms.ts";
import type { Cell, Layer } from "#canvas/types.ts";
import { emptyData, emptyFrameLayers } from "#puf/helpers.ts";
import { validateMachine } from "#puf/machines/validate.ts";
import { store } from "#puf/store/index.ts";
import { Tool } from "@pixelpuf/schemas/tool";

interface PaintLineSpec {
  brushSize: number;
  color: string;
  from: Cell;
  to: Cell;
}

interface PaintShapeSpec {
  brushSize: number;
  color: string;
  filled: boolean;
  from: Cell;
  to: Cell;
}

export function setFrameCount(totalFrames: number): void {
  const snapshot = store.getSnapshot().context;
  const clamped = Math.max(1, Math.min(64, totalFrames));
  if (clamped === snapshot.frames.length) return;

  if (clamped > snapshot.frames.length) {
    for (let index = snapshot.frames.length; index < clamped; index += 1) {
      store.trigger["frame.add"]();
    }
    return;
  }

  for (let index = snapshot.frames.length - 1; index >= clamped; index -= 1) {
    store.trigger["frame.remove"]({ index });
  }
}

export function selectFrame(frameNumber: number): void {
  store.trigger["activeFrame.set"]({ index: Math.max(0, frameNumber - 1) });
}

export function addLayer(count: number): string[] {
  const created: string[] = [];
  for (let step = 0; step < count; step += 1) {
    const ctx = store.getSnapshot().context;
    const activeFrameLayers = ctx.frames[ctx.activeFrame] ?? emptyFrameLayers();
    const newId = nextLayerId(activeFrameLayers.map((layer) => layer.id));
    const template = activeFrameLayers[0];
    store.trigger["frame.update"]({
      index: ctx.activeFrame,
      layers: [
        ...activeFrameLayers,
        {
          id: newId,
          visible: true,
          data: template ? emptyData(template.data.length, template.data[0]?.length ?? 0) : emptyData(),
        },
      ],
    });
    store.trigger["activeLayerId.set"]({ id: newId });
    created.push(newId);
  }
  return created;
}

export function selectLayer(layerId: string): boolean {
  const ctx = store.getSnapshot().context;
  const activeFrameLayers = ctx.frames[ctx.activeFrame] ?? [];
  const exists = activeFrameLayers.some((layer) => layer.id === layerId);
  if (!exists) return false;
  store.trigger["activeLayerId.set"]({ id: layerId });
  return true;
}

export function setLayerVisibility(layerId: string, visible: boolean): boolean {
  const ctx = store.getSnapshot().context;
  const activeFrameLayers = ctx.frames[ctx.activeFrame] ?? [];
  const exists = activeFrameLayers.some((layer) => layer.id === layerId);
  if (!exists) return false;
  const next = activeFrameLayers.map((layer) => (layer.id === layerId ? { ...layer, visible } : layer));
  store.trigger["frame.update"]({ index: ctx.activeFrame, layers: next });
  return true;
}

export function deleteLayer(layerId: string): boolean {
  const ctx = store.getSnapshot().context;
  const activeFrameLayers = ctx.frames[ctx.activeFrame] ?? [];
  if (activeFrameLayers.length <= 1) return false;
  const next = activeFrameLayers.filter((layer) => layer.id !== layerId);
  if (next.length === activeFrameLayers.length) return false;
  store.trigger["frame.update"]({ index: ctx.activeFrame, layers: next });
  if (ctx.activeLayerId === layerId) {
    const fallback = next[0]?.id ?? "L1";
    store.trigger["activeLayerId.set"]({ id: fallback });
  }
  return true;
}

export function selectTool(toolName: keyof typeof Tool | "PICKER"): void {
  if (toolName === "PICKER") {
    store.trigger["mode.set"]({ mode: { kind: "picker" } });
    return;
  }

  store.trigger["mode.set"]({ mode: { kind: "brush", brush: Tool[toolName] } });
}

export function setOnionEnabled(enabled: boolean): void {
  const current = store.getSnapshot().context.onion;
  if (current === enabled) return;
  store.trigger["onion.toggle"]();
}

export function clearActiveLayer(): number {
  const activeLayer = getActiveLayer();
  if (!activeLayer) return 0;
  const clearedLayer: Layer = {
    ...activeLayer.layer,
    data: activeLayer.layer.data.map((row) => row.map(() => "")),
  };
  commitLayerUpdate(activeLayer.layers, activeLayer.layerIndex, clearedLayer);
  return activeLayer.layer.data.flat().filter(Boolean).length;
}

export function paintLine(spec: PaintLineSpec): number {
  return paintCells(dilate(bresenhamLine(spec.from, spec.to), spec.brushSize), spec.color);
}

export function paintRectangle(spec: PaintShapeSpec): number {
  const cells = spec.filled ? rectFill(spec.from, spec.to) : dilate(rectOutline(spec.from, spec.to), spec.brushSize);
  return paintCells(cells, spec.color);
}

export function paintEllipse(spec: PaintShapeSpec): number {
  const cells = spec.filled
    ? ellipseFill(spec.from, spec.to)
    : dilate(ellipseOutline(spec.from, spec.to), spec.brushSize);
  return paintCells(cells, spec.color);
}

export function floodFillActiveLayer(cell: Cell, color: string): number {
  const activeLayer = getActiveLayer();
  if (!activeLayer) return 0;
  const cells = floodFill(activeLayer.layer.data, cell, color);
  return paintCells(cells, color);
}

export function validateCurrentStateMachine(): ReturnType<typeof validateMachine> | null {
  const machine = store.getSnapshot().context.stateMachine;
  if (!machine) return null;
  return validateMachine(machine);
}

export function getCurrentColor(): string {
  return store.getSnapshot().context.color;
}

export function getCurrentBrushSize(): number {
  return store.getSnapshot().context.brushSize;
}

function nextLayerId(existing: readonly string[]): string {
  let counter = existing.length + 1;
  while (existing.includes(`L${String(counter)}`)) counter += 1;
  return `L${String(counter)}`;
}

function paintCells(cells: readonly Cell[], color: string): number {
  const activeLayer = getActiveLayer();
  if (!activeLayer) return 0;

  const nextLayer: Layer = {
    ...activeLayer.layer,
    data: activeLayer.layer.data.map((row) => row.slice()),
  };
  let painted = 0;
  const seen = new Set<string>();

  for (const cell of cells) {
    const row = nextLayer.data[cell.row];
    if (!row) continue;
    if (cell.col < 0 || cell.col >= row.length) continue;
    const key = `${cell.row}|${cell.col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (row[cell.col] === color) continue;
    row[cell.col] = color;
    painted += 1;
  }

  if (painted === 0) return 0;
  commitLayerUpdate(activeLayer.layers, activeLayer.layerIndex, nextLayer);
  return painted;
}

function getActiveLayer(): { layer: Layer; layerIndex: number; layers: Layer[] } | null {
  const ctx = store.getSnapshot().context;
  const layers = ctx.frames[ctx.activeFrame] ?? [];
  const layerIndex = layers.findIndex((layer) => layer.id === ctx.activeLayerId);
  if (layerIndex < 0) return null;
  const layer = layers[layerIndex];
  if (!layer) return null;
  return { layer, layerIndex, layers };
}

function commitLayerUpdate(layers: Layer[], layerIndex: number, nextLayer: Layer): void {
  const nextLayers = layers.slice();
  nextLayers[layerIndex] = nextLayer;
  const activeFrame = store.getSnapshot().context.activeFrame;
  store.trigger["frame.update"]({ index: activeFrame, layers: nextLayers });
}
